import { useEffect, useState } from "react";
import { Activity, Eye, EyeOff, KeyRound } from "lucide-react";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { auth } from "../services/firebase";
import { trackEvent } from "../services/analytics";

const RESET_ERRORS: Record<string, string> = {
  "auth/expired-action-code": "Ez a link már lejárt.",
  "auth/invalid-action-code": "Ez a link érvénytelen, vagy már felhasználtad.",
  "auth/user-disabled": "Ez a fiók le van tiltva.",
  "auth/user-not-found": "Nem található felhasználó ehhez a linkhez.",
  "auth/weak-password": "A jelszó legalább 6 karakter legyen.",
};

function resolveResetError(code?: string): string {
  return RESET_ERRORS[code ?? ""] || "Hiba történt, próbáld újra.";
}

type Status = "checking" | "ready" | "invalid" | "success";

export default function ResetPasswordConfirm({ oobCode }: { oobCode: string }) {
  const [status, setStatus] = useState<Status>("checking");
  const [email, setEmail] = useState<string | null>(null);
  const [invalidReason, setInvalidReason] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    verifyPasswordResetCode(auth, oobCode)
      .then((verifiedEmail) => {
        if (cancelled) return;
        setEmail(verifiedEmail);
        setStatus("ready");
        trackEvent("password_reset_started");
      })
      .catch((err: any) => {
        if (cancelled) return;
        setInvalidReason(resolveResetError(err?.code));
        setStatus("invalid");
        trackEvent("password_reset_error", { error_code: err?.code || "unknown" });
      });
    return () => {
      cancelled = true;
    };
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError("A jelszó legalább 6 karakter legyen.");
      return;
    }
    if (password !== confirmPass) {
      setError("A két jelszó nem egyezik.");
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus("success");
      trackEvent("password_reset_completed");
    } catch (err: any) {
      setError(resolveResetError(err?.code));
      trackEvent("password_reset_error", { error_code: err?.code || "unknown" });
    } finally {
      setSubmitting(false);
    }
  };

  const goToLogin = () => {
    window.location.href = "/signin";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex flex-col items-center justify-center px-4">
      <div className="flex items-center gap-2 mb-8">
        <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
          <Activity size={20} className="text-white" />
        </div>
        <span className="text-2xl font-bold text-gray-900">GLP Napló</span>
      </div>

      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
        {status === "checking" && (
          <div className="flex flex-col items-center gap-3 py-6">
            <span className="animate-spin w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full" />
            <p className="text-sm text-gray-500">Link ellenőrzése...</p>
          </div>
        )}

        {status === "invalid" && (
          <div className="text-center space-y-4">
            <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto">
              <KeyRound size={24} className="text-red-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">A link nem használható</h2>
              <p className="text-sm text-gray-500">{invalidReason}</p>
            </div>
            <button
              onClick={goToLogin}
              className="w-full bg-emerald-700 text-white py-3 rounded-xl font-semibold text-sm hover:bg-emerald-800 transition-colors"
            >
              Kérj új linket
            </button>
          </div>
        )}

        {status === "ready" && (
          <>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Új jelszó megadása</h2>
              <p className="text-xs text-gray-500 mt-1">
                Új jelszó ehhez a fiókhoz: <span className="font-semibold text-gray-700">{email}</span>
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Új jelszó</label>
                <div className="relative">
                  <input
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="min. 6 karakter"
                    required
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 block mb-1">Új jelszó megerősítése</label>
                <input
                  type={showPass ? "text" : "password"}
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-emerald-700 text-white py-3 rounded-xl font-semibold text-sm hover:bg-emerald-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  "Jelszó mentése"
                )}
              </button>
            </form>
          </>
        )}

        {status === "success" && (
          <div className="text-center space-y-4">
            <p className="text-3xl">✅</p>
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Jelszó frissítve!</h2>
              <p className="text-sm text-gray-500">Mostantól az új jelszavaddal tudsz belépni.</p>
            </div>
            <button
              onClick={goToLogin}
              className="w-full bg-emerald-700 text-white py-3 rounded-xl font-semibold text-sm hover:bg-emerald-800 transition-colors"
            >
              Bejelentkezés →
            </button>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-300 mt-6">GLP Napló</p>
    </div>
  );
}
